import { FC, useMemo } from 'react';
import { format } from 'date-fns';
import { Tooltip } from 'react-tooltip';
import NumberFormat from 'react-number-format';

import { CoOwner, Encumbrance } from 'types';

import { dateFormat, numberFormat } from 'constants/formats';
import { useTranslate } from 'hooks/useTranslate';
import ShareEncumbranceViewTableCoOwnerRow from './ShareEncumbranceViewTableCoOwnerRow';
import {
  EncumbranceOption,
  encumbranceOptions,
} from '../constants/encumbranceOptions';

type Props = {
  index: number;
  encumbrance: Encumbrance;
  isEditing: boolean;
  isReadOnly?: boolean;
  mainCoOwner: CoOwner;
  // eslint-disable-next-line no-unused-vars
  handleEditEncumbrance: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  handleRemoveEncumbrance: (index: number) => void;
};

const ShareEncumbranceViewTableRow: FC<Props> = ({
  index,
  encumbrance,
  isEditing,
  isReadOnly,
  mainCoOwner,
  handleEditEncumbrance,
  handleRemoveEncumbrance,
}) => {
  const { t } = useTranslate();

  const encumbranceLabel = useMemo(() => {
    const selectedEncumbrance = encumbranceOptions.find(
      (option) => option.value === encumbrance?.name
    ) as EncumbranceOption | undefined;

    return selectedEncumbrance ? selectedEncumbrance.label : '';
  }, [encumbrance?.name]);

  const coOwnersPercent = useMemo(() => {
    const totalPercent = encumbrance?.coOwners?.reduce(
      (acc, coOwner) => acc + coOwner.percent,
      0
    );

    return totalPercent || 0;
  }, [encumbrance?.coOwners]);

  return (
    <tr>
      <td
        className="text-left text-primary"
        key={`encumbrance-name-${index}`}
        data-tooltip-id={`encumbrance-name-${index}`}
        data-tooltip-content={encumbranceLabel}
      >
        {/* [AXA] TODO: Make it dynamic based in corporate id */}
        <span className="encumbrance-name">
          {t(`ShareEncumbranceOption_${encumbranceLabel}`)}
        </span>
        <Tooltip id={`encumbrance-name-${index}`} place="top" />
      </td>

      <td className="text-center" key={`encumbrance-date-${index}`}>
        <span>
          {encumbrance?.date
            ? format(new Date(encumbrance.date), dateFormat)
            : '-'}
        </span>
      </td>

      <td className="text-center" key={`encumbrance-affected-shares-${index}`}>
        <NumberFormat
          displayType="text"
          value={encumbrance?.to - encumbrance?.from + 1}
          thousandSeparator={numberFormat.thousandSeparator}
          decimalSeparator={numberFormat.decimalSeparator}
        />
      </td>

      <td className="text-center" key={`encumbrance-from-${index}`}>
        <NumberFormat
          displayType="text"
          value={encumbrance?.from}
          thousandSeparator={numberFormat.thousandSeparator}
          decimalSeparator={numberFormat.decimalSeparator}
        />
      </td>

      <td className="text-center" key={`encumbrance-to-${index}`}>
        <NumberFormat
          displayType="text"
          value={encumbrance?.to}
          thousandSeparator={numberFormat.thousandSeparator}
          decimalSeparator={numberFormat.decimalSeparator}
        />
      </td>

      <td className="text-left" key={`encumbrance-co-owner-row-${index}`}>
        <ShareEncumbranceViewTableCoOwnerRow
          index={0}
          coOwner={{
            ...mainCoOwner,
            percent: 100 - coOwnersPercent,
          }}
          isMainCoOwner
        />

        {encumbrance?.coOwners?.map((coOwner: CoOwner, idx) => (
          <ShareEncumbranceViewTableCoOwnerRow
            index={idx + 1}
            coOwner={coOwner}
          />
        ))}
      </td>

      {!isReadOnly && (
        <td className="text-center w-100px" key={`encumbrance-edit-${index}`}>
          <button
            type="button"
            className="text-soft btn btn-icon pt-0 pb-0"
            onClick={() => handleEditEncumbrance(index)}
            disabled={isEditing}
          >
            <em className="icon ni ni-edit" />
          </button>
          <button
            type="button"
            className="text-soft btn btn-icon pt-0 pb-0"
            onClick={() => handleRemoveEncumbrance(index)}
            disabled={isEditing}
          >
            <em className="icon ni ni-trash" />
          </button>
        </td>
      )}
    </tr>
  );
};

export default ShareEncumbranceViewTableRow;
